import {
  Button,
  FormInput,
  FormNumber,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { z } from "zod";
import type {
  CreateTemplateCategoryDTO,
  UpdateTemplateCategoryDTO,
} from "../../domain/contracts/category";

const TemplateCategoryFormSchema = z.object({
  name: z.string().min(1, "ກະລຸນາປ້ອນຊື່ປະເພດ"),
  code: z.string().min(1, "ກະລຸນາປ້ອນລະຫັດປະເພດ"),
  description: z.string().optional().nullable(),
  icon: z.string().optional().nullable(),
  sortOrder: z.number().int().default(0),
});

export type TemplateCategoryFormValues = z.infer<
  typeof TemplateCategoryFormSchema
>;

export function TemplateCategoryForm({
  initialValues,
  onSubmit,
  submitting,
}: {
  initialValues?: Partial<TemplateCategoryFormValues>;
  onSubmit: (
    values: CreateTemplateCategoryDTO | UpdateTemplateCategoryDTO,
  ) => void;
  submitting?: boolean;
}) {
  const methods = RHF.useForm<TemplateCategoryFormValues>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference issue
    resolver: zodResolver(TemplateCategoryFormSchema) as any,
    defaultValues: {
      name: "",
      code: "",
      description: null,
      icon: null,
      sortOrder: 0,
      ...initialValues,
    },
  });

  return (
    <FormRoot<TemplateCategoryFormValues>
      methods={methods}
      onSubmit={(vals) =>
        onSubmit({
          name: vals.name,
          code: vals.code,
          description: vals.description ?? null,
          icon: vals.icon ?? null,
          sortOrder: vals.sortOrder ?? 0,
        })
      }
      className="space-y-4"
    >
      <FormInput
        name="name"
        label="ຊື່ປະເພດ"
        requiredMark
        placeholder="ຕົວຢ່າງ: ใบ PR, ใบ PO, ใบບິນ"
      />
      <FormInput
        name="code"
        label="ລະຫັດປະເພດ"
        requiredMark
        placeholder="ຕົວຢ່າງ: PR, PO, INVOICE"
        hint="ລະຫັດສັ້ນໆສໍາລັບປະເພດ (ຕ້ອງບໍ່ຊ້ໍາ)"
      />
      <FormTextarea
        name="description"
        label="ຄໍາອະທິບາຍ"
        placeholder="ຄໍາອະທິບາຍ (ບໍ່ບັງຄັບ)"
        rows={3}
      />
      <FormInput
        name="icon"
        label="ຮູບໄອຄອນ"
        placeholder="ຊື່ icon (ບໍ່ບັງຄັບ)"
        hint="ຕົວຢ່າງ: file-text, document, file-check"
      />
      <FormNumber
        name="sortOrder"
        label="ລໍາດັບ"
        placeholder="0"
        hint="ລໍາດັບສໍາລັບການຈັດລຽງ (ຕໍ່າກວ່າ = ສະແດງກ່ອນ)"
      />
      <div className="flex justify-end gap-2">
        <Button
          type="submit"
          disabled={submitting}
          isLoading={submitting}
        >
          ບັນທຶກ
        </Button>
      </div>
    </FormRoot>
  );
}
